// Pixel-domain watermark disruption (SynthID-class soft binding).
//
// SynthID is not metadata: it is spread across the image spectrum and survives
// light noise or a single JPEG pass. What actually degrades the correlation a
// detector computes is a *combination* of desynchronisation (crop + sub-degree
// rotation + resampling, so the watermark grid no longer lines up) and lossy
// re-encoding cycles that discard high-frequency energy. Everything below runs
// on the local canvas.

const PROFILES = {
  light: { crop: 0, rotate: 0, scale: 0.995, dither: 2, jpeg: [0.95], gamma: 0, blur: 0 },
  balanced: { crop: 0.004, rotate: 0.08, scale: 0.98, dither: 4, jpeg: [0.9], gamma: 0.01, blur: 0.1 },
  strong: { crop: 0.012, rotate: 0.22, scale: 0.93, dither: 8, jpeg: [0.84], gamma: 0.02, blur: 0.16, resynth: { keep: 0.5, grain: 4 } },
  maximum: { crop: 0.025, rotate: 0.45, scale: 0.84, dither: 12, jpeg: [0.7, 0.9], gamma: 0.035, blur: 0.22, resynth: { keep: 0.25, grain: 7 } },
  // Extreme replaces almost the whole detail layer: visible softening + grain,
  // the closest a browser gets to the diffusion redraw used by GPU workflows.
  extreme: { crop: 0.04, rotate: 0.8, scale: 0.72, dither: 15, jpeg: [0.58, 0.9], gamma: 0.05, blur: 0.32, resynth: { keep: 0.08, grain: 10 } },
};

const clamp = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
const mimeOf = (format) => (format === "png" ? "image/png" : "image/jpeg");

function canvasOf(w, h) {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  return { c, ctx };
}

const encode = (canvas, mime, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, mime, quality));

async function decode(blob) {
  return createImageBitmap(blob);
}

/** crop + sub-degree rotation: breaks the watermark's spatial synchronisation. */
function desync(bitmap, p) {
  const w = bitmap.width;
  const h = bitmap.height;
  const cw = Math.round(w * (1 - 2 * p.crop));
  const ch = Math.round(h * (1 - 2 * p.crop));
  const { c, ctx } = canvasOf(cw, ch);
  ctx.translate(cw / 2, ch / 2);
  if (p.rotate) ctx.rotate((p.rotate * Math.PI) / 180);
  // slight over-draw so the rotation never exposes empty corners
  const over = 1 + 2 * p.crop + Math.abs(p.rotate) / 40;
  ctx.drawImage(bitmap, (-cw * over) / 2, (-ch * over) / 2, cw * over, ch * over);
  return c;
}

/** downscale then upscale: destroys the high-frequency carrier. */
function resample(source, p) {
  const w = source.width;
  const h = source.height;
  const small = canvasOf(w * p.scale, h * p.scale);
  small.ctx.drawImage(source, 0, 0, small.c.width, small.c.height);
  const full = canvasOf(w, h);
  full.ctx.drawImage(small.c, 0, 0, w, h);
  if (p.blur > 0) {
    const soft = canvasOf(w, h);
    soft.ctx.filter = `blur(${(0.7).toFixed(1)}px)`;
    soft.ctx.drawImage(full.c, 0, 0);
    soft.ctx.filter = "none";
    full.ctx.save();
    full.ctx.globalAlpha = p.blur;
    full.ctx.drawImage(soft.c, 0, 0);
    full.ctx.restore();
  }
  return full;
}

/** per-channel dither + per-channel gamma jitter: no channel keeps its curve. */
function perturbPixels({ c, ctx }, p) {
  const img = ctx.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  const g = [1 + p.gamma, 1 - p.gamma * 0.6, 1 + p.gamma * 0.3];
  const lut = g.map((gamma) => {
    const t = new Uint8Array(256);
    for (let v = 0; v < 256; v++) t[v] = clamp(Math.round(255 * (v / 255) ** (1 / gamma)));
    return t;
  });
  const amp = p.dither;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = clamp(lut[0][d[i]] + (Math.random() * 2 - 1) * amp);
    d[i + 1] = clamp(lut[1][d[i + 1]] + (Math.random() * 2 - 1) * amp);
    d[i + 2] = clamp(lut[2][d[i + 2]] + (Math.random() * 2 - 1) * amp);
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/**
 * Noise-layer resynthesis — the local approximation of the diffusion redraw used
 * by SynthID-bypass workflows: SynthID lives in the low-level noise, so the
 * detail layer is discarded and rebuilt from fresh noise while the structure
 * layer (composition, edges) is kept.
 */
function resynthesizeNoise({ c, ctx }, keep = 0.3, grain = 6) {
  const w = c.width;
  const h = c.height;
  const original = ctx.getImageData(0, 0, w, h);

  const base = canvasOf(w, h);
  base.ctx.filter = "blur(1.1px)";
  base.ctx.drawImage(c, 0, 0);
  base.ctx.filter = "none";
  const structure = base.ctx.getImageData(0, 0, w, h);

  const o = original.data;
  const s = structure.data;
  for (let i = 0; i < o.length; i += 4) {
    for (let k = 0; k < 3; k++) {
      const detail = o[i + k] - s[i + k];
      o[i + k] = clamp(s[i + k] + detail * keep + (Math.random() * 2 - 1) * grain);
    }
  }
  ctx.putImageData(original, 0, 0);
  return { c, ctx };
}

export async function disruptPixels(bytes, format, strength = "balanced") {
  const base = PROFILES[strength] || PROFILES.balanced;
  const png = format === "png";
  // PNG inputs must not pay for a hard lossy intermediate: a second resample
  // cycle desynchronises the carrier just as well with far less visible loss.
  const p = png && base.jpeg.length > 1 ? { ...base, jpeg: [0.86, 1], extraResample: 0.92 } : base;
  const outMime = mimeOf(format);
  let bitmap = await decode(new Blob([bytes], { type: outMime }));

  const desynced = p.crop || p.rotate ? desync(bitmap, p) : bitmap;
  let resampled = resample(desynced, p);
  if (p.extraResample) resampled = resample(resampled.c, { ...p, scale: p.extraResample, blur: 0 });
  if (p.resynth) resampled = resynthesizeNoise(resampled, p.resynth.keep, p.resynth.grain);
  const perturbed = perturbPixels(resampled, p);
  bitmap.close?.();

  const terms = [
    p.crop ? `crop ${(p.crop * 200).toFixed(1)}%` : null,
    p.rotate ? `rotate ${p.rotate}°` : null,
    `resample ×${p.scale}`,
    p.extraResample ? `resample ×${p.extraResample}` : null,
    p.resynth ? `noise-layer resynthesis (keep ${p.resynth.keep})` : null,
    `±${p.dither} dither`,
    p.gamma ? `gamma ${p.gamma}` : null,
    `${p.jpeg.length} re-encode pass(es)`,
  ].filter(Boolean);
  const action = `pixel-domain disruption (${strength}): ${terms.join(" + ")}`;

  // Lossy re-encode cycles: each pass throws away more of the spectrum the
  // detector correlates against.
  let canvas = perturbed;
  for (let i = 0; i < p.jpeg.length; i++) {
    const q = p.jpeg[i];
    const isLast = i === p.jpeg.length - 1;
    const blob = await encode(canvas, isLast ? outMime : "image/jpeg", q);
    if (isLast) return { bytes: new Uint8Array(await blob.arrayBuffer()), action };
    const bm = await decode(blob);
    const next = canvasOf(bm.width, bm.height);
    next.ctx.drawImage(bm, 0, 0);
    bm.close?.();
    canvas = next.c;
  }
  const blob = await encode(canvas, outMime, 0.92);
  return { bytes: new Uint8Array(await blob.arrayBuffer()), action };
}

export const isRasterFormat = (f) => f === "png" || f === "jpeg";
